import * as $ from "jquery";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import { BaseController } from "../../_BaseViews";
import { DashboardChannel } from "./../../../models/DashboardChannel";
import { GetSeriesOptions } from "./ChartWorkerManager";
import { BaseDashboardWidgetViewOptions } from "./_BaseWidgetView";
import {
    CHART_AGGREGATION_PERIOD,
    CHART_AGGREGATION_TYPE,
    CHART_CONVERT_ALL_DATA,
    CHART_DATA_RANGE,
    CHART_HIDE_SINGLE_GAPS,
    CHART_LABELS,
    CHART_REAL_TIMESTAMPS,
    CHART_THRESHOLDS,
    CHART_TYPE,
    ChartConfig,
    ChartHelper,
    DAILY_TIME_MODE,
    minMaxTimeStampOptions
} from "./chart/ChartHelper";

//Interface created for a future adaptation to chart widgets
interface AggregationWidgetManagerOptions extends BaseDashboardWidgetViewOptions {
}

export class AggregationWidgetManager extends BaseController {

    options: AggregationWidgetManagerOptions;

    protected lastRenderedChannelIds: number[] = [];
    protected channelsSelected: number[] = [];
    protected lastFilterValue: string = null;
    /* What's the earliest/latest we've ever requested data from */
    protected minDataRequested = {};
    protected maxDataRequested = {};
    /* Maps an index (e.g. a dashboard channel id) to a workerId */
    protected workers: { [key: string]: string } = {};
    protected chartConfig: ChartConfig = null;
    /* Which channels are currently graphed */
    protected graphedDashboardChannelIds: number[] = [];
    protected dateTimePickerFormat = 'MM/DD/YYYY h:mm A';

    protected aggregationChannelsValues = [];

    protected aggregationPeriodLabels = {
        '8': '15 Minutes',
        '9': '30 Minutes',
        '1': '1 Hour',
        '2': '1 Day',
        '3': '1 Week',
        '4': '1 Month',
        '5': '3 Months',
        '6': '6 Months',
        '7': '1 Year'
    };
    protected aggregationTypeLabels = {
        '1': 'Avg',
        '2': 'Sum',
        '3': 'Min',
        '4': 'Max',
        '5': 'Delta',
        '6': 'Mode',
        '7': 'Median',
        '8': 'SD',
        '11': '95th',
        '12': '5th'
    };

    constructor(options: AggregationWidgetManagerOptions) {
        super(options);
    }

    public checkAggregation() {
        var outerDeferred = $.Deferred();

        var agg_type = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', '');
        var agg_period = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', '');

        var channels = <DashboardChannel[]>this.options.dashboardChannelCollection.where({ dashboard_widget_id: this.options.widgetId });

        if (agg_type && agg_period || agg_period === CHART_AGGREGATION_PERIOD.NONE) {
            var promises = _.map(channels, (channel) => {
                var result = this.calculateMinMax(channel);
                var index = String(channel.get('dashboard_channel_id'));
                return this.fetchDataForIndex(channels, index, result[0], result[1]);
            });
            $.when.apply($, promises).then(() => {
                outerDeferred.resolve(this.aggregationChannelsValues);
            });
        } else {
            outerDeferred.resolve();
        }
        return outerDeferred;
    }

    public getPeriodLabel(id: string) {
        return this.aggregationPeriodLabels[id];
    }

    public getTypeLabel(id: string) {
        return this.aggregationTypeLabels[id];
    }

    protected fetchDataForIndex(channels, index, min: number, max?: number) {
        this.minDataRequested[index] = Math.min(this.minDataRequested[index], min);
        this.aggregationChannelsValues = [];
        var ids: number[] = [];
        _.each(channels, (channel) => {
            ids.push(channel.get('dashboard_channel_id'))
            this.workers[index] = this.options.chartWorkerManager.registerChannelWorker();
            this.graphedDashboardChannelIds.push(channel.get('dashboard_channel_id'))
        });

        this.chartConfig = {
            seriesGroups: [],
            timezone: ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser),
            showThresholdPlotlines: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_THRESHOLDS', CHART_THRESHOLDS.DEFAULT)),
            realTimestamps: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_TIMESTAMPS', CHART_REAL_TIMESTAMPS.DEFAULT)),
            hideSingleGaps: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_HIDE_SINGLE_GAPS', CHART_HIDE_SINGLE_GAPS.DEFAULT)),
            convertAllValues: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_CONVERT_ALL_DATA', CHART_CONVERT_ALL_DATA.DEFAULT)),
            labels: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_LABELS', CHART_LABELS.DEFAULT))
        };
        this.generateSeriesGroupConfigs();

        var d = $.Deferred<void>();
        var getSeriesOptions: GetSeriesOptions = {
            workerId: this.workers[index],
            dashboardChannelIds: ids,
            chartConfig: this.chartConfig,
            startTimestamp: this.minDataRequested[index],
            keepData: true,
            limitDataPoints: null,
        };

        if (max) getSeriesOptions['endTimestamp'] = max;
        // Handle daily custom range
        var dataRange = this.options.widget.getParamValue('CHART_DATA_RANGE', CHART_DATA_RANGE.DEFAULT);
        var fullRange = this.options.widget.getParamValue('DAILY_TIME_MODE', DAILY_TIME_MODE.DEFAULT);
        if (max && dataRange === CHART_DATA_RANGE.CUSTOM_DAILY && fullRange < 2 && max > moment().unix()) {
            // If we haven't completed today's range yet, switch to tomorrow
            var timezone = ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser);
            getSeriesOptions['endTimestamp'] = moment(Number(max) * 1000).tz(timezone).subtract(1, 'day').unix();
            getSeriesOptions['startTimestamp'] = moment(Number(min) * 1000).tz(timezone).subtract(1, 'day').unix();
        } else if (max) {
            getSeriesOptions['endTimestamp'] = max;
        }

        this.options.chartWorkerManager.getSeries(getSeriesOptions, (error, data) => {
            _.each(ids, (id: number) => {
                this.aggregationChannelsValues = data;
            });
            d.resolve();
        });

        return d.promise();
    }

    private calculateMinMax(channel) {
        var index = channel.get('dashboard_channel_id');
        if (this.minDataRequested[index] == null) {
            this.minDataRequested[index] = Infinity
        }
        if (this.maxDataRequested[index] == null) {
            this.maxDataRequested[index] = null
        }

        // Figure out when to load data from
        var min, max;
        var initial = this.minDataRequested[index] === Infinity;
        var baseMinMaxOptions = {
            initial: initial,
            channel: channel,
            widget: this.options.widget,
            customDataRangeFunc: this.getCustomDataRangeInput.bind(this),
            isDefaultChart: false,
            dashboardChannelCollection: this.options.dashboardChannelCollection,
            graphedDashboardChannelIds: this.graphedDashboardChannelIds,
            user: this.options.currentUser
        };
        var maxOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
            maxDataRequested: this.maxDataRequested[index]
        });
        max = ChartHelper.getMaxTimestampToRequest(maxOptions);
        var minOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
            minDataRequested: this.minDataRequested[index],
            maxDataRequested: max
        });

        min = ChartHelper.getMinTimestampToRequest(minOptions);

        return [min, max];
    }

    protected getCustomDataRangeInput() {
        var timezone = ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser);
        var fromText = (<any>this.$('.chart-data-range .from')).val();
        var toText = (<any>this.$('.chart-data-range .to')).val();
        var from = moment.tz(fromText, this.dateTimePickerFormat, timezone);
        var to = moment.tz(toText, this.dateTimePickerFormat, timezone);

        return {
            from: from,
            to: to,
            fromText: fromText,
            toText: toText
        }
    }

    protected generateSeriesGroupConfigs(dashboardChannelIdsToGraph?: number[]) {
        if (!dashboardChannelIdsToGraph) {
            dashboardChannelIdsToGraph = [];
            var widgetId = this.options.widget.get('id');
            this.options.dashboardChannelCollection.each((c) => {
                if (c.get('dashboard_widget_id') === widgetId) {
                    dashboardChannelIdsToGraph.push(c.get('dashboard_channel_id'))
                }
            });
        }

        _.each(dashboardChannelIdsToGraph, (id) => {
            this.addToSeriesGroups(id);
        });
    }

    protected addToSeriesGroups(dashboardChannelId: number) {
        var chartTypes, aggregationTypes, period, showUnit;

        // Generate the config from widget params or from the ui?
        var typeParam = this.options.widget.getParamValue('CHART_TYPE', CHART_TYPE.DEFAULT);
        chartTypes = _.map(_.split(typeParam, ','));

        var aggregationParam = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', CHART_AGGREGATION_TYPE.DEFAULT);
        aggregationTypes = _.map(_.split(aggregationParam, ','));

        period = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', CHART_AGGREGATION_PERIOD.DEFAULT);
        showUnit = ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_CONVERT_ALL_DATA', CHART_CONVERT_ALL_DATA.DEFAULT));

        var channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: Number(dashboardChannelId) });
        var secondary_order = channel.get('dashboard_channel_secondary_order');
        var order = channel.get('dashboard_channel_order');
        var decimals = channel.get('channel_formatted_decimals') || 2;
        var suffix = showUnit ? channel.get('channel_unit') : '';
        var name = channel.get('dashboard_channel_name');
        var channelId = channel.get('channel_id');

        // Default to the first type/period if there is
        // not the right number of periods in the array
        var aggType, type;
        if (period === CHART_AGGREGATION_PERIOD.NONE) {
            aggType = CHART_AGGREGATION_TYPE.NONE;
        } else if (order && order < aggregationTypes.length) {
            aggType = aggregationTypes[order];
        } else {
            aggType = aggregationTypes[0];
        }
        if (secondary_order && secondary_order < chartTypes.length) {
            type = chartTypes[secondary_order];
        } else {
            type = chartTypes[0];
        }

        var series = {
            name: name,
            valueSuffix: suffix,
            valueDecimals: decimals,
            agg: aggType,
            tag: dashboardChannelId,
            channelId: channelId,
        };
        var gIndex = _.findIndex(this.chartConfig.seriesGroups, (<any>{ period: period, type: type }));

        if (gIndex >= 0) {
            this.chartConfig.seriesGroups[gIndex].series.push(series)
        } else {
            this.chartConfig.seriesGroups.push({
                period: period,
                type: type,
                series: [series],
            })
        }
    }

}


// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/AggregationWidgetManager.ts